import { CampaignType } from "@/@types/campaign";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Filter } from "lucide-react";

// Mesmo visual do filtro da listagem de campanhas, restrito aos tipos que
// suportam comparação (PRO012 é legado/read-only e não propaga).
const TYPES = [
  {
    value: CampaignType.PRO013,
    label: "Leve X Pague Y",
    color: "bg-purple-100 text-purple-700",
  },
  {
    value: CampaignType.PRO014,
    label: "Promoção Itens",
    color: "bg-blue-100 text-blue-700",
  },
  {
    value: CampaignType.PRO017,
    label: "Combo Moderno",
    color: "bg-green-100 text-green-700",
  },
];

interface ComparisonCampaignTypeFilterProps {
  selectedTypes: CampaignType[];
  onTypesChange: (types: CampaignType[]) => void;
}

export function ComparisonCampaignTypeFilter({
  selectedTypes,
  onTypesChange,
}: ComparisonCampaignTypeFilterProps) {
  const handleToggle = (type: CampaignType) => {
    const newTypes = selectedTypes.includes(type)
      ? selectedTypes.filter((t) => t !== type)
      : [...selectedTypes, type];
    onTypesChange(newTypes);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 border-dashed">
          <Filter className="mr-2 h-4 w-4" />
          Tipo de Campanha
          {selectedTypes.length > 0 && (
            <>
              <div className="mx-2 h-4 w-[1px] bg-gray-300" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal lg:hidden"
              >
                {selectedTypes.length}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedTypes.length > 2 ? (
                  <Badge
                    variant="secondary"
                    className="rounded-sm px-1 font-normal"
                  >
                    {selectedTypes.length} selecionados
                  </Badge>
                ) : (
                  TYPES.filter((type) =>
                    selectedTypes.includes(type.value)
                  ).map((type) => (
                    <Badge
                      key={type.value}
                      variant="secondary"
                      className={`rounded-sm px-1 font-normal ${type.color}`}
                    >
                      {type.label}
                    </Badge>
                  ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <div className="space-y-4 p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Tipo de Campanha</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onTypesChange(TYPES.map((t) => t.value))}
              >
                Todos
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => onTypesChange([])}
                disabled={selectedTypes.length === 0}
              >
                Limpar
              </Button>
            </div>
          </div>
          <div className="space-y-3">
            {TYPES.map((type) => (
              <div key={type.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`comparison-type-${type.value}`}
                  checked={selectedTypes.includes(type.value)}
                  onCheckedChange={() => handleToggle(type.value)}
                />
                <Label
                  htmlFor={`comparison-type-${type.value}`}
                  className="flex flex-1 cursor-pointer items-center justify-between text-sm font-normal"
                >
                  <span>{type.label}</span>
                  <span className="font-mono text-xs text-muted-foreground/60">
                    {type.value}
                  </span>
                </Label>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
